import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemePreferenceContextType {
  themePreference: ThemeMode;
  setThemePreference: (mode: ThemeMode) => void;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextType>({
  themePreference: 'light',
  setThemePreference: () => {},
});

export const useThemePreference = () => useContext(ThemePreferenceContext);

interface ThemePreferenceProviderProps {
  children: ReactNode;
}

export const ThemePreferenceProvider: React.FC<ThemePreferenceProviderProps> = ({ children }) => {
  const [themePreference, setThemePreference] = useState<ThemeMode>(() => {
    // Try to get theme from localStorage
    const savedTheme = localStorage.getItem('themeMode');
    
    // If not found, check user's system preference
    if (!savedTheme) {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      return 'light';
    }
    
    // Validate the saved theme
    return (savedTheme === 'dark' || savedTheme === 'light') ? savedTheme : 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', themePreference);
  }, [themePreference]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only change theme automatically if user hasn't explicitly set a preference
      if (!localStorage.getItem('themeMode')) {
        setThemePreference(e.matches ? 'dark' : 'light');
      }
    };
    
    // Add listener for theme changes
    mediaQuery.addEventListener('change', handleChange);
    
    // Clean up
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <ThemePreferenceContext.Provider value={{ themePreference, setThemePreference }}>
      {children}
    </ThemePreferenceContext.Provider>
  );
};